import React, { useEffect } from "react";

import {
  Box,
  Button,
  CompassColor,
  Heading,
  Image,
  Spacing,
  Stack,
  StackItem,
  Text,
  useMediaQuery,
} from "@noom/wax-component-library";

import { QuestionComponentProps } from "@/components/survey/Question";
import { useTrackEvent } from "@/hooks";

import { BaseOutline } from "./BaseOutline";

const DEFAULT_AUTO_ADVANCE_MS = 6000;

type BaseInterstitialProps = {
  autoAdvance?: boolean;
  autoAdvanceMs?: number;
  backgroundColor?: CompassColor | string;
  centerChildren?: boolean;
  children?: React.ReactNode;
  color?: CompassColor;
  ctaText?: string;
  footerText?: string;
  headerText?: string;
  helperText?: string;
  imageAlt?: string;
  imageSrc?: string;
  invertBackButton?: boolean;
  onClickBack: () => void;
  onClickNext: QuestionComponentProps["onClickNext"];
  questionId: string;
  showBackButton?: boolean;
  width?: string;
  margin?: string;
};

export const BaseInterstitial: React.FC<BaseInterstitialProps> = ({
  autoAdvance = false,
  autoAdvanceMs = DEFAULT_AUTO_ADVANCE_MS,
  backgroundColor,
  centerChildren = false,
  children,
  color,
  ctaText,
  footerText,
  headerText,
  helperText,
  imageAlt,
  imageSrc,
  invertBackButton = false,
  onClickBack,
  onClickNext,
  questionId,
  showBackButton = false,
  width = "400px",
  margin,
}) => {
  const [isBrowser] = useMediaQuery("(min-width: 1160px)");
  const { trackAnonEvent, trackAnonPageViewed } = useTrackEvent();

  const handleClickBack = () => {
    trackAnonEvent("b2bEnrollmentAnonButtonClicked", {
      buttonName: "backArrow",
      pageId: questionId,
    });

    onClickBack();
  };

  const handleClickCta = () => {
    trackAnonEvent("b2bEnrollmentAnonButtonClicked", {
      buttonName: ctaText || "",
      pageId: questionId,
    });

    onClickNext(null);
  };

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (autoAdvance) {
      timeoutId = setTimeout(() => onClickNext(null), autoAdvanceMs);
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    trackAnonPageViewed(questionId);
  }, [questionId]);

  return (
    <BaseOutline backgroundColor={backgroundColor}>
      <Box
        display="flex"
        flexDirection="column"
        flexGrow={centerChildren ? 1 : 0}
        margin={margin || "var(--spacing-m)"}
        color={color}
      >
        {showBackButton && (
          <button type="button" tabIndex={-1}>
            <Image
              alt="Back"
              cursor="pointer"
              onClick={handleClickBack}
              paddingBottom="var(--spacing-m)"
              src="/assets/img/left-arrow.svg"
              filter={invertBackButton ? "brightness(0) invert(1)" : undefined}
            />
          </button>
        )}
        <Stack
          margin="auto"
          maxWidth={{
            base: "400px",
            lg: width,
          }}
          padding={
            !showBackButton
              ? {
                  lg: "var(--spacing-xxl) 0",
                }
              : undefined
          }
          spacing={isBrowser ? Spacing[8] : Spacing[4]}
        >
          {(headerText || helperText) && (
            <Stack spacing={Spacing[4]}>
              {headerText && (
                <Heading fontSize="24px" fontWeight={500} lineHeight="32px">
                  {headerText}
                </Heading>
              )}
              {helperText && <Text>{helperText}</Text>}
            </Stack>
          )}
          {imageSrc ? (
            <Image
              alt={imageAlt}
              margin="auto"
              src={imageSrc}
              width="fit-content"
            />
          ) : (
            children
          )}
          {ctaText && (
            <StackItem display="flex" justifyContent="center">
              <Button
                autoFocus
                colorScheme="primary"
                marginBottom="var(--spacing-m)"
                marginTop={{
                  base: "var(--spacing-xl)",
                  lg: "0px",
                }}
                onClick={handleClickCta}
                size="xl"
                type="submit"
                _focus={{
                  boxShadow: "none",
                }}
              >
                {ctaText}
              </Button>
            </StackItem>
          )}
        </Stack>
        {footerText && (
          <Box
            bottom={0}
            paddingBottom={isBrowser ? "var(--spacing-m)" : "0px"}
            position={isBrowser ? "absolute" : "static"}
          >
            <Text color={CompassColor.black} fontSize="14px" lineHeight="18px">
              {footerText}
            </Text>
          </Box>
        )}
      </Box>
    </BaseOutline>
  );
};
